<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('admin-relays')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Provision Relay</h1>
        </header>

        <section class="module__body">
            <form
                class="provision-form"
                @submit.prevent="submit"
            >
                <h2>Provisioning Fields</h2>
                <div class="form-group">
                    <label for="provision-name">Name</label>
                    <input
                        id="provision-name"
                        v-model="provision_name"
                        data-test-id="provision-name-field"
                        type="text"
                        maxlength="191"
                        minlength="3"
                        required
                    />
                </div>
                <div class="form-group">
                    <label
                        for="provision-description"
                        data-test-id="provision-description"
                        >Description</label
                    >
                    <textarea
                        id="provision-description"
                        v-model="provision_description"
                        class="short"
                        data-test-id="provision-description-field"
                        minlength="3"
                        maxlength="191"
                    />
                </div>
                <div class="form-group">
                    <label for="provision-license-count">Asset Count</label>
                    <input
                        id="provision-license-count"
                        v-model="provision_license_count"
                        data-test-id="provision-license-count-field"
                        type="number"
                        min="1"
                        required
                    />
                </div>
                <div class="form-group">
                    <label for="provision-password">Password</label>
                    <input
                        id="provision-password"
                        v-model="provision_password"
                        data-test-id="provision-password-field"
                        type="password"
                        required
                        maxlength="60"
                    />
                </div>
                <div class="form-group">
                    <label for="password_confirmation">Confirm Password</label>
                    <input
                        id="password_confirmation"
                        v-model="provision_password_confirmation"
                        data-test-id="provision-password-confirmation-field"
                        type="password"
                        required
                        maxlength="60"
                    />
                </div>
                <h2>Relay Fields</h2>
                <div class="form-group">
                    <create-relay-form
                        :force-relay-type="relayTypes.ADVANCED.type"
                        :force-smart-device="true"
                        :hide-actions="true"
                        :hide-images="true"
                        @form-updated="relayFormUpdated"
                    />
                </div>
                <form-errors :response="errorResponse"></form-errors>
                <div class="form-group">
                    <button
                        :class="['btn', 'btn-primary', { active: isSubmitted }]"
                        data-test-id="provision-download-button"
                        type="submit"
                    >
                        <div>Download</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import _ from 'lodash'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import CreateRelayForm from '@/components/CreateRelay.vue'
import FormErrors from '@/components/FormErrors.vue'
import relayTypes from '@/constants/relay-types'
import canToast from '@/mixins/canToast'

export default {
    components: {
        CreateRelayForm,
        FormErrors,
    },

    mixins: [hasBackButton, needsOrganizationSlug, canToast],

    data() {
        return {
            provision_name: '',
            provision_description: '',
            provision_password: '',
            provision_password_confirmation: '',
            provision_license_count: null,
            relayData: null,
            isSubmitted: false,
            errorResponse: null,
            relayTypes,
        }
    },

    computed: {},

    methods: {
        relayFormUpdated(formData) {
            const relayFieldWhitelist = [
                'site_name',
                'asset_number',
                'relay_serial_number',
                'installed_at',
                'installed_by',
                'latitude',
                'longitude',
                'altitude',
                'description',
                'is_tower_mounted',
                'power_type',
                'solar_power_panel_type',
                'solar_power_wattage',
                'ac_power_circuit_amps',
                'ac_power_source_type',
                'ac_power_voltage',
                'is_battery_external',
                'battery_type',
                'type',
                'radios',
            ]
            this.relayData = _.pick(formData, relayFieldWhitelist)
        },
        submit() {
            this.isSubmitted = true

            this.$http
                .post(
                    `/${this.organizationSlug}/certificates/provisioning-profiles`,
                    {
                        provision_name: this.provision_name,
                        provision_description: this.provision_description,
                        provision_password: this.provision_password,
                        provision_password_confirmation: this.provision_password_confirmation,
                        provision_license_count: this.provision_license_count,
                        ...this.relayData,
                    },
                    {
                        responseType: 'blob',
                    }
                )
                .then((response) => {
                    this.toast('Provisioning Profile Created!')

                    const contentDisposition = response.headers['content-disposition']
                    let filename = 'download.json'

                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^"]+)"?/)
                        const [, extractedFilename] = match

                        if (extractedFilename) {
                            filename = extractedFilename
                        }
                    }

                    // Make a temporary link and click it
                    const href = window.URL.createObjectURL(response.data)
                    const link = document.createElement('a')

                    link.href = href
                    link.setAttribute('download', filename) // Set the file name
                    document.body.appendChild(link) // Append to body to make it clickable in all browsers
                    link.click()

                    // Cleanup
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(href)
                    this.isSubmitted = false
                    this.errorResponse = null
                })
                .catch(async (error) => {
                    const responseText = await error.response.data.text()
                    this.isSubmitted = false
                    this.errorResponse = { data: JSON.parse(responseText) }
                })
        },
    },
}
</script>
<style scoped lang="scss">
.form-group-header {
    font-size: 15px;
    font-weight: 800;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    height: 24px;
}
form h2 {
    margin-bottom: 20px;
    font-family: 'Avenir Heavy', Helvetica, Arial, sans-serif;
    font-size: 2.125rem;
    line-height: 1.5;
}
</style>
<style>
.provision-form .relay-form .relay-details-header {
    display: none;
}
</style>
